import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Fade from '@material-ui/core/Fade';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PeopleIcon from '@material-ui/icons/People';
import { Tooltip, CircularProgress } from '@material-ui/core';
import { get } from 'lodash';
import classnames from 'classnames';

import { withTranslation } from '../../src/i18n';

import {
  selectActiveProfile,
  selectUserData,
  selectUserProfiles,
  selectIsUserProfilesLoading,
  selecIsSwitchingProfile,
} from '../../containers/App/selectors';
import { setActiveProfileAction } from '../../containers/App/actions';
import { colors } from '../../constants/styleConstants';
import { TP } from '../../constants/consts';
import route from '../../constants/routes';
import NextLink from '../NextLink';
import usePermissions from '../../utils/permissions';
import adminIcon from '../../public/images/adminAccountIcon.png';
import ActionStatusModal from '../ActionStatusModal';
import ProfileMenuItem from '../ProfileMenuItem';
import ImageTag from '../ImageTag';

const useStyles = makeStyles(() => ({
  menu: {
    padding: 0,
    minWidth: 200,
    border: '1px solid rgba(0, 0, 0, 0.2)',
  },
  selectButton: {
    backgroundColor: 'transparent',
    border: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '&:focus': {
      outline: 'none',
    },
  },
  logoutLink: {
    display: 'block',
    padding: '8px 16px',
    color: colors.title,
    '&:hover': {
      color: colors.title,
      textDecoration: 'none',
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    borderBottom: `1px solid ${colors.linesGray}`,
  },
  lastItem: {
    borderBottom: 'none',
  },
  linkWithLabel: {
    display: 'flex',
    alignContent: 'middle',
    fontWeight: 'normal',
  },
  linkLabel: {
    margin: 'auto 10px auto 0',
  },
  linkValue: {
    margin: 'auto 0',
  },
  assosTitle: {
    fontSize: '13px',
    fontWeight: 400,
    padding: '0 10px',
  },
  adminAccess: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'middle',
    fontSize: '16px',
    marginTop: 5,
  },
  profilesLoadingIndicator: {
    textAlign: 'center',
  },
  profileList: {
    maxHeight: 310,
    overflowY: 'auto',
  },
  adminAccountBadge: {
    width: 25,
    height: 25,
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    fontWeight: 'bold',
    padding: '6px 16px',
    borderBottom: `1px solid ${colors.linesGray}`,
  },
}));

const ProfilesMenuDropdown = ({
  t,
  activeProfile,
  profilesList,
  userData,
  isProfileListLoading,
  isProfileChanging,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onMenuItemClick = (profileId, path, as) => {
    if (!path && activeProfile?.id === profileId) return;
    dispatch(setActiveProfileAction({ profileId, path, as }));
    handleClose();
  };

  const displayName = useMemo(
    () =>
      get(activeProfile, 'name') ??
      get(activeProfile, 'companies[0].company.name') ??
      get(activeProfile, 'agencies[0].name') ??
      get(activeProfile, 'user.name'),
    [activeProfile],
  );

  const { isAdmin } = usePermissions();
  return (
    <div>
      <ActionStatusModal
        isOpened={isProfileChanging}
        onClose={() => {}}
        type="progress"
        title={t(`${TP}.FN_PROFILE_SWITCHING`)}
      />
      <button
        type="button"
        aria-controls="profiles-menu"
        aria-haspopup="true"
        onClick={handleClick}
        className={classes.selectButton}
      >
        {isAdmin && (
          <Tooltip title="Admin account">
            <>
              <ImageTag src={adminIcon} className={classes.adminAccountBadge} alt="admin account" />
            </>
          </Tooltip>
        )}
        {displayName}
        {` (${get(userData, 'email')})`}
        <ExpandMoreIcon style={{ fill: colors.blueLight }} />
      </button>
      <Menu
        id="profiles-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        TransitionComponent={Fade}
        classes={{
          list: classes.menu,
        }}
      >
        {isAdmin && (
          <div className={classes.adminAccess}>
            <ImageTag src={adminIcon} className={classes.adminAccountBadge} alt="admin account" /> Admin account
          </div>
        )}
        <p className={classes.assosTitle}>{t(`${TP}.RELATED_ACCOUNTS_HEADER`).replace('%s', userData?.email)}</p>
        <div className={classes.profileList}>
          {isProfileListLoading ? (
            <div className={classes.profilesLoadingIndicator}>
              <CircularProgress />
            </div>
          ) : (
            profilesList.map(profile => (
              <MenuItem
                key={`profile-${profile.id}`}
                onClick={() => onMenuItemClick(profile.id)}
                classes={{ root: classes.menuItem }}
                selected={profile.id === activeProfile?.id}
              >
                <ProfileMenuItem
                  profile={profile}
                  onProfileSelect={(path, as) => onMenuItemClick(profile.id, path, as)}
                />
              </MenuItem>
            ))
          )}
        </div>
        <NextLink className={classes.logoutLink} href={`${route.ACCOUNT_SETTINGS}`}>
          <div className={classes.linkWithLabel}>
            <PeopleIcon className={classes.linkLabel} />
            <div className={classes.linkValue}>{t(`${TP}.MANAGE_YOUR_ACCOUNT`)}</div>
          </div>
        </NextLink>
        <NextLink className={classnames(classes.logoutLink, classes.lastItem)} href={`${route.LOGOUT}`}>
          <div className={classes.linkWithLabel}>
            <ExitToAppIcon className={classes.linkLabel} />
            <div className={classes.linkValue}>{t(`${TP}.m_LOGOUT`)}</div>
          </div>
        </NextLink>
      </Menu>
    </div>
  );
};

ProfilesMenuDropdown.propTypes = {
  t: PropTypes.func.isRequired,
  activeProfile: PropTypes.object,
  userData: PropTypes.object,
  profilesList: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  activeProfile: selectActiveProfile(),
  userData: selectUserData(),
  profilesList: selectUserProfiles(),
  isProfileListLoading: selectIsUserProfilesLoading(),
  isProfileChanging: selecIsSwitchingProfile(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(withTranslation(), withConnect)(ProfilesMenuDropdown);
